import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  SelectableProhibitedKeywordList,
  type SelectableKeywordItem,
} from "@/components/shared/SelectableProhibitedKeywordList";
import {
  useBulkDeleteProhibitedKeywords,
  useDeleteProhibitedKeyword,
} from "@/resources/prohibited-keyword/prohibited-keyword.api";
import type { ProhibitedKeyword } from "@/types/prohibited-keyword";

interface ProhibitedKeywordsListProps {
  brandId: number | null;
  brandLabel: string;
  keywords: ProhibitedKeyword[];
  isLoading: boolean;
}

export function ProhibitedKeywordsList({ brandId, brandLabel, keywords, isLoading }: ProhibitedKeywordsListProps) {
  const [search, setSearch] = useState("");

  const deleteMutation = useDeleteProhibitedKeyword();
  const bulkDeleteMutation = useBulkDeleteProhibitedKeywords();

  const filteredKeywords = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...keywords].sort((a, b) => a.keyword.localeCompare(b.keyword));
    if (!q) return sorted;
    return sorted.filter((k) => k.keyword.includes(q));
  }, [keywords, search]);

  // AUT-220: the single-remove confirm closes as soon as the request is fired (the shared
  // list does that); the delete is optimistic and the server work behind it can run long.
  const handleRemove = (item: SelectableKeywordItem, callbacks: { onSuccess: () => void }) => {
    if (!brandId) return;
    deleteMutation.mutate({ id: item.id, brandId }, callbacks);
  };

  const handleRemoveMany = (ids: number[]) => {
    if (!brandId) return;
    bulkDeleteMutation.mutate({ brandId, ids });
  };

  return (
    <div className="space-y-3">
      <h5 className="text-base font-semibold leading-6 text-muted-foreground">
        Brand List
      </h5>

      {keywords.length > 0 && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search keywords..."
            className="pl-8 pr-8"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-sm text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      <SelectableProhibitedKeywordList
        key={brandId ?? "none"}
        items={filteredKeywords}
        totalCount={keywords.length}
        isLoading={isLoading}
        scopeLabel={`for ${brandLabel}`}
        layout="narrow"
        removingId={deleteMutation.isPending ? (deleteMutation.variables?.id ?? null) : null}
        onRemove={handleRemove}
        onRemoveMany={handleRemoveMany}
      />
    </div>
  );
}
